import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';

type Tone = 'warning' | 'error' | 'info';

interface AlertBannerAction {
  label: string;
  to: string;
  /** Open in a new tab, so leaving does not throw away what the person was doing here. */
  newTab?: boolean;
}

const toneStyles: Record<
  Tone,
  { box: string; icon: string; text: string; action: string; defaultIcon: LucideIcon }
> = {
  warning: {
    box: 'border-amber-200 dark:border-warning-dark/30 bg-amber-50 dark:bg-warning-dark-tint',
    icon: 'text-amber-500 dark:text-warning-dark',
    text: 'text-amber-800 dark:text-warning-dark',
    action: 'border-amber-300 dark:border-warning-dark/30 text-amber-800 dark:text-warning-dark hover:bg-amber-100 dark:hover:bg-warning-dark-tint',
    defaultIcon: AlertTriangle,
  },
  error: {
    box: 'border-red-200 dark:border-error-dark/30 bg-red-50 dark:bg-error-dark-tint',
    icon: 'text-red-500 dark:text-error-dark',
    text: 'text-red-700 dark:text-error-dark',
    action: 'border-red-300 dark:border-error-dark/30 text-red-700 dark:text-error-dark hover:bg-red-100 dark:hover:bg-error-dark-tint',
    defaultIcon: AlertCircle,
  },
  info: {
    box: 'border-line bg-tint',
    icon: 'text-tint-fg',
    text: 'text-ink-strong',
    action: 'border-line-input text-ink-strong hover:bg-chip',
    defaultIcon: Info,
  },
};

/**
 * Small, self-contained inline alert banner for setup/health notices. Tone drives
 * the border/background/text colours and the default leading icon; an optional
 * action renders on the right as a compact link styled to match the tone.
 */
export function AlertBanner({
  tone,
  icon,
  children,
  action,
}: {
  tone: Tone;
  icon?: LucideIcon;
  children: ReactNode;
  action?: AlertBannerAction;
}) {
  const styles = toneStyles[tone];
  const Icon = icon ?? styles.defaultIcon;
  return (
    <div role="alert" className={cn('flex items-start gap-3 rounded-xl border px-4 py-3', styles.box)}>
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', styles.icon)} aria-hidden />
      <p className={cn('flex-1 text-sm leading-relaxed', styles.text)}>{children}</p>
      {action && (
        <Link
          to={action.to}
          {...(action.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          className={cn(
            'focus-ring shrink-0 self-center rounded-lg border bg-card/60 px-3 py-1 text-xs font-medium transition-colors',
            styles.action,
          )}
        >
          {action.label}
          {action.newTab && <span className="sr-only"> (opens in a new tab)</span>}
        </Link>
      )}
    </div>
  );
}
